import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { useNavigate, useLocation } from 'react-router-dom';
import RaiseIssueChat from './RaiseIssueChat';
import { isLendingNetworkPath } from '../MobileBottomNav';

/**
 * Floating "Raise an Issue" launcher.
 *
 * Mounted in App.js's ProtectedRoute so it appears on every authenticated
 * page but never on login / landing / public pages.
 *
 * Layout: bottom-right pill, doesn't conflict with PrimeReact's bottom-
 * right Toast slot (Toast comes in from the top by default in this app).
 * z-index sits above the main app shell but below modals (which use
 * PrimeReact's default 1100+).
 *
 * Vertical placement has to dodge two other fixed elements that own the same
 * corner (see raiseIssueFabStyles below): the layout config gear and, on
 * Lending Network pages under 992px, the mobile bottom nav.
 */
const raiseIssueFabStyles = `
  /* This pill is the BOTTOM of the FAB stack; the config gear sits above it at
     5.1rem (see .layout-config-button in styles/layout/_config.scss — the two
     sets of offsets have to move together). */
  .raise-issue-fab {
    position: fixed;
    right: 1.5rem;
    bottom: 1.5rem;
    z-index: 900;
    padding: 0.85rem 1.2rem;
    height: auto;
    font-weight: 600;
    font-size: 0.92rem;
    display: flex;
    align-items: center;
    gap: 0.55rem;
  }
  /* Below 992px on Lending Network routes the mobile bottom nav owns the bottom
     edge (~3.75rem + iOS safe area), so the whole stack shifts up. */
  @media screen and (max-width: 991px) {
    .raise-issue-fab.has-bottom-nav {
      bottom: calc(4.9rem + env(safe-area-inset-bottom, 0px));
    }
  }
  /* Narrow phones: drop the label so the pill can't run off-screen. */
  @media screen and (max-width: 380px) {
    .raise-issue-fab { padding: 0.85rem; }
    .raise-issue-fab .raise-issue-fab-label { display: none; }
  }
`;

const RaiseIssueLauncher = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // MobileBottomNav renders only on these paths, so only there does the pill
  // need to clear it.
  const hasBottomNav = isLendingNetworkPath(location.pathname);

  return (
    <>
      <style>{raiseIssueFabStyles}</style>
      <Button
        type="button"
        className={`raise-issue-fab p-button-rounded p-button-primary shadow-3${hasBottomNav ? ' has-bottom-nav' : ''}`}
        onClick={() => setOpen(true)}
        aria-label="Raise an issue"
      >
        <span
          aria-hidden="true"
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#fbbf24',
            boxShadow: '0 0 0 2px rgba(251,191,36,.25)',
            flexShrink: 0,
          }}
        />
        <i className="pi pi-comment" aria-hidden="true"></i>
        <span className="raise-issue-fab-label">Raise an Issue</span>
      </Button>

      <RaiseIssueChat
        visible={open}
        onHide={() => setOpen(false)}
        onNavigateMyIssues={() => navigate('/app/support/my-issues')}
      />
    </>
  );
};

export default RaiseIssueLauncher;
